/**
 * Utility functions for handling UTM parameters and affiliate tracking
 */

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

export interface TrackingParams extends UTMParams {
  affiliate?: string;
  __distinct_id?: string; // PostHog distinct ID for cross-domain tracking
}

/**
 * Extracts UTM parameters from the current URL search params
 * This function works on the client side only
 */
export function getUTMParams(): UTMParams {
  if (typeof window === 'undefined') {
    return {};
  }

  const searchParams = new URLSearchParams(window.location.search);
  const utmParams: UTMParams = {};

  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

  utmKeys.forEach(key => {
    const value = searchParams.get(key);
    if (value) {
      utmParams[key] = value;
    }
  });

  return utmParams;
}

/**
 * Gets the affiliate parameter from URL first, then localStorage as fallback
 * Implements "first contact wins" logic - returns the stored affiliate if it exists
 */
export function getAffiliate(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  // Check localStorage first (first contact wins)
  const storedAffiliate = localStorage.getItem('affiliate');
  if (storedAffiliate) {
    return storedAffiliate;
  }

  // If no stored affiliate, check URL
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get('affiliate');
}

/**
 * Extracts all tracking parameters (UTM + affiliate + PostHog distinct_id) from the current URL
 * Falls back to localStorage for affiliate if not in URL
 * This function works on the client side only
 */
export function getTrackingParams(): TrackingParams {
  if (typeof window === 'undefined') {
    return {};
  }

  const searchParams = new URLSearchParams(window.location.search);
  const trackingParams: TrackingParams = {};

  // Get UTM parameters
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
  utmKeys.forEach(key => {
    const value = searchParams.get(key);
    if (value) {
      trackingParams[key] = value;
    }
  });

  // Get affiliate parameter (uses getAffiliate which checks localStorage first)
  const affiliate = getAffiliate();
  if (affiliate) {
    trackingParams.affiliate = affiliate;
  }

  // Get PostHog distinct_id for cross-domain tracking
  // @ts-ignore - PostHog is loaded globally
  if (typeof window !== 'undefined' && window.posthog) {
    // @ts-ignore
    const distinctId = window.posthog.get_distinct_id();
    if (distinctId) {
      trackingParams.__distinct_id = distinctId;
    }
  }

  return trackingParams;
}

/**
 * Appends UTM parameters to a given URL
 */
export function appendUTMParams(baseUrl: string, utmParams: UTMParams): string {
  const url = new URL(baseUrl);

  Object.entries(utmParams).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value);
    }
  });

  return url.toString();
}

/**
 * Appends all tracking parameters (UTM + affiliate) to a given URL
 */
export function appendTrackingParams(baseUrl: string, trackingParams: TrackingParams): string {
  const url = new URL(baseUrl);

  Object.entries(trackingParams).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value);
    }
  });

  return url.toString();
}

/**
 * Gets all tracking parameters (UTM + affiliate) from current URL and appends them to a target URL
 * This is a convenience function that combines both operations
 * Only adds parameters that are not already present in the target URL
 */
export function forwardUTMParams(targetUrl: string): string {
  const currentTrackingParams = getTrackingParams();

  // If no tracking parameters in current URL, return original URL
  if (Object.keys(currentTrackingParams).length === 0) {
    return targetUrl;
  }

  const url = new URL(targetUrl);

  // Only add tracking parameters that are not already present in the target URL
  Object.entries(currentTrackingParams).forEach(([key, value]) => {
    if (value && !url.searchParams.has(key)) {
      url.searchParams.set(key, value);
    }
  });

  return url.toString();
}